// Deal Pipeline spec §5.4 — drill-through filter contract.
//
// Every chart on /deal-flow/metrics navigates to
//   /deal-flow/pipeline?filter={base64(JSON)}
// when clicked. The Pipeline page decodes the
// payload and applies the supported criteria to its DealListQuery. Unknown
// keys are tolerated — the page strips them silently so future charts
// can pre-load filter shapes without coordinating a Pipeline UI release.

import type { CriteriaLane, FailureStatus } from "@/api/dealFlow/types";
import type { DealListQuery, DealPipelineStage } from "@/types/deals";

export type DealFlowFailureStatus = FailureStatus;

export interface PipelineFilterCriteria {
  stage?: DealPipelineStage;
  brokerId?: string;
  analystUserId?: string;
  failureLane?: CriteriaLane;
  failureStatus?: DealFlowFailureStatus;
  failureCode?: string;
  dqrBucket?: 1 | 2 | 3 | 4 | 5;
  dealSizeBand?: string;
  cycleTimeBand?: string;
  cohortQuarter?: string;
  fromDate?: string;
  toDate?: string;
}

/**
 * Encodes a filter as a UTF-8-safe base64 string. The unescape/escape
 * pair is the established pattern for handling non-ASCII payloads in
 * `btoa`; using `btoa(JSON.stringify(...))` directly fails the moment a
 * filter value contains a non-Latin-1 character.
 */
export function encodeFilter(filter: PipelineFilterCriteria): string {
  const json = JSON.stringify(filter);
  return btoa(unescape(encodeURIComponent(json)));
}

export function decodeFilter(encoded: string): PipelineFilterCriteria {
  try {
    const json = decodeURIComponent(escape(atob(encoded)));
    const parsed = JSON.parse(json);
    if (parsed && typeof parsed === "object") {
      return parsed as PipelineFilterCriteria;
    }
  } catch {
    // Bad payload — fall through to empty filter.
  }
  return {};
}

/** Convenience: build the full drill-through URL. */
export function buildPipelineDrillThroughUrl(
  filter: PipelineFilterCriteria,
): string {
  const encoded = encodeFilter(filter);
  return `/deal-flow/pipeline?filter=${encoded}`;
}

/**
 * Applies a drill-through to the Pipeline list's query: the stage, broker and
 * analyst it names replace the current ones. A drill into Declined also shows
 * ratified declines, which the list hides by default: a deal is declined when
 * its stage is Declined, and confirming a workflow decline moves it there
 * (owner ruling 18; Q8, 2026-10-12), so the Declined bar counts them.
 */
export function applyDrillThrough(prev: DealListQuery, decoded: PipelineFilterCriteria): DealListQuery {
  return {
    ...prev,
    stage: decoded.stage ? [decoded.stage] : prev.stage,
    brokerId: decoded.brokerId ?? prev.brokerId,
    assignedA1Id: decoded.analystUserId ?? prev.assignedA1Id,
    ...(decoded.stage === "Declined" ? { status: "all" as const } : {}),
  };
}
